import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {Router} from '@angular/router';
import {MuacUtil} from '../../util/muac.util';
import {WomanCaseModel} from '../../model/woman-case.model';
import {VisitModel} from '../../model/visit.model';
import {WomanCaseService} from '../../services/woman-case.service';
import {LoadingService} from '../../services/loading.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {FilterUtil} from '../../util/filter.util';
import {WomenService} from '../../services/women.service';
import algoliasearch from 'algoliasearch';
import {AlgoliaUtil} from '../../util/algolia.util';
import {AlgoliaService} from '../../services/algolia.service';
import {LoginService} from '../../services/login.service';
import {ProvinceService} from '../../services/province.service';
import {CountyService} from '../../services/county.service';
import {FilterWomanService} from '../../services/filter-woman.service';
import {DateUtil} from '../../util/date.util';
import {User} from '../../model/user.model';
import {NextVisitPeriod} from '../../constants/next-visit-period';
import {UserService} from '../../services/user.service';
import {WomenVisitsCsvContentBuilder} from '../../builder/export/csv/women/women-visits-csv-content-builder';
import {WomenCsvContentBuilder} from '../../builder/export/csv/women/women-csv-content-builder';

export declare interface Searchable {
    search(clear: boolean);

    getFilters(): Array<string>;
}

@Component({
    selector: 'app-women',
    templateUrl: './women.component.html',
    styleUrls: ['./women.component.scss']
})
export class WomenComponent implements OnInit, OnDestroy, Searchable {

    order = 0;
    orders = {0: 'Próxima visita', 1: 'Nombre', 2: 'MUAC'};

    indexByName;
    indexByVisitDate;
    indexByMuac;
    indexWomanVisits;

    searchTerm = '';
    searching = false;
    filterBy: FilterUtil;
    results: WomanCaseModel[];
    totalResults = 0;
    totalCases = 0;
    profileFilter = '';
    currPage = 0;
    numberOfPages = 0;
    hitsPerPage = 15;

    exporting = false;
    exportingProgress = 0;

    nextVisitPeriods = NextVisitPeriod.values();
    nextVisitStartDate: Date;
    nextVisitEndDate: Date;

    selectedPeriod: string;
    customPeriodSearch = false;

    @Input()
    public user: User;

    // ---- Barra de filtros (replicada de Children) ----
    showPeriodMenu = false;
    periodDraftStart: Date;
    periodDraftEnd: Date;
    datepickerOpts = {
        language: 'es-ES',
        showTodayBtn: true,
        todayBtnTxt: 'Hoy',
        todayBtn: new Date(),
        todayHighlight: true,
        autoclose: true,
        format: 'dd/mm/yyyy',
        placeholder: 'dd/mm/yyyy',
        icon: 'fa fa-calendar'
    };
    private hasActiveTextSearch = false;

    // Protocolo de registro — aparece no modal, ainda inerte (newProtocol quase
    // sem dados no índice de mulheres, mesma situação de Children).
    readonly protocolFilterEnabled = true;
    protocolFilter: 'all' | 'new' | 'old' = 'all';
    protocolFilterOptions: {key: 'all' | 'new' | 'old', label: string, description?: string}[] = [
        {
            key: 'all', label: 'Todos',
            description: 'Incluye casos de ambos protocolos: el simplificado (solo MUAC) y el nuevo ' +
                '(MUAC, peso, talla, sexo y edad).'
        },
        {
            key: 'new', label: 'Nuevo protocolo',
            description: 'Evalúa MUAC, peso, talla, sexo y edad. Es la nueva versión de la app y el portal.'
        },
        {
            key: 'old', label: 'Protocolo simplificado',
            description: 'Evalúa solo el MUAC, con cortes fijos. Corresponde a la versión anterior de la app y el portal.'
        }
    ];

    // Tipo de caso — filtro real via facet `type` do Algolia.
    womanTypeFilter: 'all' | 'pregnant' | 'lactating' = 'all';
    womanTypeFilterOptions: {key: 'all' | 'pregnant' | 'lactating', label: string, description?: string}[] = [
        {key: 'all', label: 'Todas'},
        {
            key: 'pregnant', label: 'Embarazadas',
            description: 'Solo mujeres en seguimiento durante el embarazo.'
        },
        {
            key: 'lactating', label: 'Lactantes',
            description: 'Solo mujeres en seguimiento durante la lactancia.'
        }
    ];

    // Legenda dos pontos de "Visitas:" — marcadores de mulheres.
    visitLegend: {class: string, label: string}[] = [
        {class: 'acute-malnutrition', label: 'Desnutrición aguda'},
        {class: 'outside-risk-zone', label: 'Fuera de la zona de riesgo'},
        {class: 'healed', label: 'Recuperada'},
        {class: 'no-measured', label: 'Sin medición o visita futura'}
    ];

    constructor(private algoliaService: AlgoliaService,
                private provinceService: ProvinceService,
                private countyService: CountyService,
                private loadingService: LoadingService,
                private womenService: WomenService,
                private womanService: WomanCaseService,
                private loginService: LoginService,
                private modalService: NgbModal,
                private filterWomanService: FilterWomanService,
                private router: Router,
                private userService: UserService,
                private translocoService: TranslocoService) {
        const client = algoliasearch(AlgoliaUtil.APP_ID, AlgoliaUtil.getSearchApiKey());
        this.indexByName = client.initIndex('woman_order_by_name');
        this.indexByMuac = client.initIndex('woman_order_by_muac');
        this.indexWomanVisits = client.initIndex('woman_visits');
        this.indexByVisitDate = client.initIndex(AlgoliaUtil.getWomenIndex());
        this.selectedPeriod = NextVisitPeriod.NEXT_SEVEN_DAYS.key;
        filterWomanService.setSearchable = this;
    }

    ngOnInit(): void {
        this.algoliaService.getFilterByProfile(this.loginService.authUser).then(filter => {
            this.profileFilter = filter;
            this.getTotalCases();
            this.search(true);
        });
    }

    ngOnDestroy() {

    }

    setOrder(order) {
        this.order = parseInt(order);
        this.search(true);
    }

    isSupervisor() {
        return this.loginService.getCurrentUser().isSupervisor;
    }

    isNationalSupervisor() {
        return this.loginService.isCurrentUserUnicefOrSNS(this.loginService.authUser);
    }

    trackByScopeId(_index: number, item: { id: number }) {
        return item.id;
    }

    muac(value: number) {
        return MuacUtil.getWomanMuacClassification(value);
    }

    generateVisits(visits: Array<VisitModel>): Array<VisitModel> {
        const numberOfVisits = visits.length;
        const markers = new Array<string>();
        for (let i = 0; i < visits.length; i++) {
            markers.push(visits[i].marker);
            visits[i].order = i;
            visits[i].stages = [...markers];
        }
        for (let i = 0; i < (5 - numberOfVisits); i++) {
            visits.push(this.emptyVisit());
        }
        return visits;
    }

    emptyVisit(): VisitModel {
        const visit = new VisitModel();
        visit.marker = 'no-measured';
        return visit;
    }

    isLactating(womanCase: WomanCaseModel) {
        return this.womanService.isLactating(womanCase);
    }

    showWomanProfile(womanCase: WomanCaseModel) {
        this.router.navigate(['app/woman-profile/', womanCase.token]);
    }

    getIndex() {
        switch (this.order) {
            case 0:
                return this.indexByVisitDate;
            case 1:
                return this.indexByName;
            case 2:
                return this.indexByMuac;
            default:
                return this.indexByVisitDate;
        }
    }

    getBasicFilter() {
        if (this.user != null) {
            return `currentAssistantUid:${this.user.uid}`;
        } else {
            return this.profileFilter;
        }
    }

    getFilters(): Array<string> {
        let filters = new Array();

        filters = [this.getBasicFilter()];

        if (this.filterWomanService.provincesSelected.length > 0) {
            // algoliaProvinceName resolve a divergência de acento (ex.: "Moseñor Nouel"
            // no cadastro vs "Mosenor Nouel" gravado em ~182 casos).
            filters = [...filters, this.filterWomanService.provincesSelected.map(
                p => (`province:${this.filterWomanService.algoliaProvinceName(p)}`))];
        }

        if (this.filterWomanService.countysSelected.length > 0) {
            filters = [...filters, this.filterWomanService.countysSelected.map(p => (`county:${p}`))];
        }

        if (this.filterWomanService.organizationSelected.length > 0) {
            filters = [...filters, this.filterWomanService.organizationSelected.map(p => (`organizationName:${p}`))];
        }

        if (this.filterWomanService.filtersSelected.length > 0) {
            let filtersOrs = [];
            this.filterWomanService.filtersSelected.forEach(filterInt => {
                const filterObj = this.filterWomanService.filterOptions.find(f => f.id == filterInt);
                if (filterInt < 4) {
                    if (filterInt == 3) {
                        filtersOrs.push('transferredCase:true');
                    } else {
                        filtersOrs.push('visits.alertIcon.class:' + filterObj.class);
                    }
                } else {
                    filtersOrs.push('marker:' + filterObj.class);
                }
            });
            filters = [...filters, filtersOrs];
        }

        // Tipo de caso — facet `type` guarda o i18nkey ('woman.pregnant' / 'woman.lactating').
        if (this.womanTypeFilter !== 'all') {
            filters = [...filters, `type:woman.${this.womanTypeFilter}`];
        }

        return filters;
    }

    exportList() {
        this.exporting = true;
        this.convertToCsv().then(csvData => {
            this.exporting = false;
            let blob = new Blob(['\ufeff' + csvData], {type: 'text/csv;charset=utf-8;'});
            let dwldLink = document.createElement('a');
            let url = URL.createObjectURL(blob);
            let isSafariBrowser = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1;
            if (isSafariBrowser) {
                dwldLink.setAttribute('target', '_blank');
            }
            dwldLink.setAttribute('href', url);
            dwldLink.setAttribute('download', 'mujeres-' + DateUtil.nowFormated('YYYY-MM-DD') + '.csv');
            dwldLink.style.visibility = 'hidden';
            document.body.appendChild(dwldLink);
            dwldLink.click();
            document.body.removeChild(dwldLink);
        }).catch(ex => {
            this.exporting = false;
            console.error('exporting_error', ex);
        });
    }

    async convertToCsv(): Promise<String> {
        const headers = [
            'Mujer',
            'Fecha de Nascimento',
            'Muac',
            'Provincia',
            'Municipio',
            'Organización',
            'User UID',
            'Nivel de Desnutrición',
            'Alerta',
            'Hospital',
            'Transferido',
            'Primeira Visita',
            'Tipo',
            'Usuario NOMBRE',
            'Usuario PROVINCIA',
            'Usuario MUNICIPIO',
            'Token del Caso',
            'Dirección',
            'Telefono',
            'Cedula',
            'Enfermidad Crónica'
        ].join(';') + '\r\n';

        const women = await this.algoliaService.getAllResults(
            this.getIndex(),
            this.searchTerm,
            this.getSearchConfigExport(),
            (progress) => {
                this.exportingProgress = Math.floor(progress * 100) / 100;
            }
        );

        const data = women.flatMap(data => data.hits.map(value => ({...value})));
        const contentBuilder = new WomenCsvContentBuilder(data);
        const csvContent = contentBuilder.build();
        return headers + csvContent;
    }

    exportVisitsList() {
        this.exporting = true;
        this.convertVisitsToCsv().then(csvData => {
            this.exporting = false;
            let blob = new Blob(['\ufeff' + csvData], {type: 'text/csv;charset=utf-8;'});
            let dwldLink = document.createElement('a');
            let url = URL.createObjectURL(blob);
            let isSafariBrowser = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1;
            if (isSafariBrowser) {
                dwldLink.setAttribute('target', '_blank');
            }
            dwldLink.setAttribute('href', url);
            dwldLink.setAttribute('download', 'mujeres-visitas-' + DateUtil.nowFormated('YYYY-MM-DD') + '.csv');
            dwldLink.style.visibility = 'hidden';
            document.body.appendChild(dwldLink);
            dwldLink.click();
            document.body.removeChild(dwldLink);
        }).catch(ex => {
            this.exporting = false;
        });
    }

    async convertVisitsToCsv(): Promise<string> {
        const headers = [
            'Token del Caso',
            'MUAC',
            'Resultado',
            'Próxima Visita',
            'Hospital',
            'Semana',
            'Usuario UID',
            'Usuario NOMBRE',
            'Usuario PROVINCIA',
            'Usuario MUNICIPIO',
            'Organizatión',
            'Semana de Gestación',
            'Fecha',
            'Orden de Visita',
            'Comentarios',
            'Instruciones',
            'Síntomas'
        ].join(';') + '\r\n';

        const visitResults = await this.algoliaService.getAllResults(
            this.indexWomanVisits,
            '',
            this.getCasesSearchConfigExport(),
            (progress: number) => {
                this.exportingProgress = Math.floor(progress * 100) / 100;
            }
        );

        const visitData = visitResults.flatMap(data => data.hits.map(value => ({ ...value })));
        const contentBuilder = new WomenVisitsCsvContentBuilder(visitData, this.translocoService);
        const csvContent = contentBuilder.build();
        return headers + csvContent;
    }

    getSearchConfigExport() {
        const facetFilters = this.getFilters();
        return {
            filters: this.getMalnutritionFilters(),
            facetFilters: facetFilters,
            hitsPerPage: this.hitsPerPage * 100,
            page: 0
        };
    }

    getMalnutritionFilters() {
        return this.filterWomanService.onlyMalnutritionCases ? 'visits.marker:acute-malnutrition' : '';
    }

    getCasesSearchConfigExport() {
        return {
            filters: '',
            facetFilters: [''],
            hitsPerPage: this.hitsPerPage * 100,
            attributesToRetrieve: [
                'caseId',
                'muac',
                'marker',
                'nextVisit',
                'fowardToHospital',
                'week',
                'province',
                'county',
                'organizationName',
                'gestationWeek',
                'date',
                'order',
                'healthAssistant',
                'comments',
                'instructions',
                'symptoms'
            ],
            page: 0
        };
    }

    async search(clear: boolean) {
        if (clear) {
            this.clearResults();
        }
        this.searching = true;
        const config = this.getSearchConfig();
        await this.getIndex().search(this.searchTerm, config).then(data => {
            this.searching = false;
            this.totalResults = data.nbHits;
            this.numberOfPages = data.nbPages;
            this.results = this.results.concat(data.hits);
        });
    }

    getSearchConfig() {
        const facetFilters = this.getFilters();
        let filters = this.getMalnutritionFilters();
        if (filters.length == 0) {
            filters = this.getNextVisitPeriodFilter();
        } else if (NextVisitPeriod.ALL.key != this.selectedPeriod) {
            filters = filters.concat(' AND ' + this.getNextVisitPeriodFilter());
        }

        return {
            facets: ['token'],
            filters: filters,
            facetFilters: facetFilters,
            hitsPerPage: this.hitsPerPage,
            page: this.currPage
        };
    }

    getNextVisitPeriodFilter() {
        if (NextVisitPeriod.ALL.key == this.selectedPeriod) {
            return;
        }
        const date1 = new Date(this.nextVisitStartDate);
        const date2 = new Date(this.nextVisitEndDate);
        if (this.nextVisitStartDate) {
            date1.setHours(0, 0, 0);
        }

        if (this.nextVisitEndDate) {
            date2.setHours(23, 59, 59);
        }

        const nextVisitStartDate = date1.getTime();
        const nextVisitEndDate = date2.getTime();
        if (!this.selectedPeriod) {
            const inSevenDays = DateUtil.addDays(new Date(), 7);
            return `nextVisitTimestamp >= ${DateUtil.todayFirstTimestampOfDay()} AND nextVisitTimestamp <= ${inSevenDays.getTime()}`;
        }

        if (this.selectedPeriod == 'CUSTOM' && (this.nextVisitStartDate || this.nextVisitEndDate)) {
            let query = '';
            if (this.nextVisitStartDate && this.nextVisitEndDate) {
                query = `nextVisitTimestamp >= ${nextVisitStartDate} AND nextVisitTimestamp <= ${nextVisitEndDate}`;
            } else if (this.nextVisitStartDate && !this.nextVisitEndDate) {
                query = `nextVisitTimestamp >= ${nextVisitStartDate}`;
            } else if (!this.nextVisitStartDate && this.nextVisitEndDate) {
                query = `nextVisitTimestamp <= ${nextVisitEndDate}`;
            }
            return query;
        }

        switch (this.selectedPeriod) {
            case NextVisitPeriod.NEXT_SEVEN_DAYS.key:
                const inSevenDays = DateUtil.addDays(new Date(), 7);
                return `NOT marker:healed AND nextVisitTimestamp >= ${DateUtil.nowTimestamp()} AND nextVisitTimestamp <= ${inSevenDays.getTime()}`;
            case NextVisitPeriod.NEXT_THIRTY_DAYS.key:
                const inThirtyDays = DateUtil.addDays(new Date(), 30);
                return `NOT marker:healed AND nextVisitTimestamp >= ${DateUtil.todayFirstTimestampOfDay()} AND nextVisitTimestamp <= ${inThirtyDays.getTime()}`;
            case NextVisitPeriod.LAST_SEVEN_DAYS.key:
                const lastSevenDays = DateUtil.minusDays(new Date(), 7);
                return `NOT marker:healed AND nextVisitTimestamp >= ${lastSevenDays.getTime()} AND nextVisitTimestamp <= ${DateUtil.nowTimestamp()}`;
            case NextVisitPeriod.TODAY.key:
                return `NOT marker:healed AND nextVisitTimestamp >= ${DateUtil.todayFirstTimestampOfDay()} AND nextVisitTimestamp <= ${DateUtil.todayLastTimestampOfDay()}`;
            case NextVisitPeriod.CURRENT_MONTH.key:
                const firstDayOfMonth = DateUtil.getFirstDayOfMonth();
                const lastDayOfMonth = DateUtil.getLastDayOfMonth();
                return `NOT marker:healed AND nextVisitTimestamp >= ${firstDayOfMonth.getTime()} AND nextVisitTimestamp <= ${lastDayOfMonth.getTime()}`;
            case NextVisitPeriod.CURRENT_YEAR.key:
                const firstDayOfYear = DateUtil.getFirstDayOfYear();
                const lastDayOfYear = DateUtil.getLastDayOfYear();
                return `NOT marker:healed AND nextVisitTimestamp >= ${firstDayOfYear.getTime()} AND nextVisitTimestamp <= ${lastDayOfYear.getTime()}`;
            case NextVisitPeriod.LAST_YEAR.key:
                const firstDayOfLastYear = DateUtil.getFirstDayOfLastYear();
                const lastDayOfLastYear = DateUtil.getLastDayOfLastYear();
                return `NOT marker:healed AND nextVisitTimestamp >= ${firstDayOfLastYear.getTime()} AND nextVisitTimestamp <= ${lastDayOfLastYear.getTime()}`;
            case NextVisitPeriod.ALL.key:
                return '';
        }
    }

    getTotalCases() {
        const facetFilters = [this.getBasicFilter()];
        this.indexByVisitDate.search('', {
            facetFilters: facetFilters,
            hitsPerPage: 0,
            attributesToRetrieve: [],
            attributesToHighlight: [],
            facets: []
        }).then(data => {
            this.totalCases = data.nbHits;
        });
    }

    setHitsPerPage(hitsPerPage) {
        this.hitsPerPage = hitsPerPage;
        this.search(true);
    }

    clearSearch() {
        this.searchTerm = '';
        this.hasActiveTextSearch = false;
        this.search(true);
    }

    onSearchSubmit() {
        if (this.searchTerm && this.searchTerm.trim().length > 0) {
            this.hasActiveTextSearch = true;
            this.search(true);
        }
    }

    onSearchTermChange() {
        if (this.hasActiveTextSearch && (!this.searchTerm || this.searchTerm.trim().length === 0)) {
            this.hasActiveTextSearch = false;
            this.search(true);
        }
    }

    setProtocolFilter(key: 'all' | 'new' | 'old') {
        this.protocolFilter = key;
        this.search(true);
    }

    setWomanTypeFilter(key: 'all' | 'pregnant' | 'lactating') {
        this.womanTypeFilter = key;
        this.search(true);
    }

    clearResults() {
        this.currPage = 0;
        this.results = [];
    }

    async findNext() {
        this.currPage = this.currPage + 1;
        this.search(false);
    }

    lastPageReached() {
        return ((this.currPage + 1) >= this.numberOfPages);
    }


    filterByPeriod(event) {
        this.selectedPeriod = event;
        if (!this.customPeriodSearch && this.selectedPeriod == NextVisitPeriod.CUSTOM.key) {
            this.customPeriodSearch = true;
            return;
        }
        this.search(true);
    }

    cancelFilterByPeriod() {
        this.selectedPeriod = NextVisitPeriod.NEXT_SEVEN_DAYS.key;
        this.customPeriodSearch = false;
        this.nextVisitStartDate = null;
        this.nextVisitEndDate = null;
        this.search(true);
    }

    // ---- Dropdown de "Próxima visita" (mesmo padrão de Children) ----
    get activePeriodLabel(): string {
        const key = this.selectedPeriod || NextVisitPeriod.NEXT_SEVEN_DAYS.key;
        const found = this.nextVisitPeriods.find(p => p.key === key);
        return found ? found.label : NextVisitPeriod.NEXT_SEVEN_DAYS.label;
    }

    togglePeriodMenu() {
        this.showPeriodMenu = !this.showPeriodMenu;
        if (this.showPeriodMenu) {
            setTimeout(() => this.focusPeriodMenuItem(0));
        }
    }

    closePeriodMenu() {
        if (!this.showPeriodMenu) {
            return;
        }
        this.showPeriodMenu = false;
        const trigger = document.querySelector('.period-trigger') as HTMLElement;
        if (trigger) {
            trigger.focus();
        }
    }

    onPeriodMenuKeydown(event: KeyboardEvent) {
        if (!this.showPeriodMenu) {
            return;
        }
        const items = this.getPeriodMenuItems();
        if (!items.length) {
            return;
        }
        const current = items.indexOf(document.activeElement as HTMLElement);
        event.preventDefault();
        if (event.key === 'ArrowDown') {
            this.focusPeriodMenuItem(current < 0 ? 0 : (current + 1) % items.length);
        } else if (event.key === 'ArrowUp') {
            this.focusPeriodMenuItem(current <= 0 ? items.length - 1 : current - 1);
        }
    }

    private getPeriodMenuItems(): HTMLElement[] {
        return Array.from(document.querySelectorAll('.period-menu button')) as HTMLElement[];
    }

    private focusPeriodMenuItem(index: number) {
        const item = this.getPeriodMenuItems()[index];
        if (item) {
            item.focus();
        }
    }

    selectPeriodOrCustom(key: string, periodRangeModal: any) {
        if (key === NextVisitPeriod.CUSTOM.key) {
            this.prepPeriodDraft();
            if (periodRangeModal) {
                periodRangeModal.show();
            }
            return;
        }
        this.selectPeriod(key);
    }

    selectPeriod(key: string) {
        this.showPeriodMenu = false;
        this.selectedPeriod = key;
        this.customPeriodSearch = false;
        this.nextVisitStartDate = null;
        this.nextVisitEndDate = null;
        this.search(true);
    }

    prepPeriodDraft() {
        this.showPeriodMenu = false;
        this.periodDraftStart = this.nextVisitStartDate;
        this.periodDraftEnd = this.nextVisitEndDate;
    }

    applyPeriodRange() {
        this.nextVisitStartDate = this.periodDraftStart;
        this.nextVisitEndDate = this.periodDraftEnd;
        this.selectedPeriod = NextVisitPeriod.CUSTOM.key;
        this.customPeriodSearch = true;
        this.search(true);
    }

    clearPeriodRange() {
        this.selectedPeriod = NextVisitPeriod.NEXT_SEVEN_DAYS.key;
        this.customPeriodSearch = false;
        this.nextVisitStartDate = null;
        this.nextVisitEndDate = null;
        this.periodDraftStart = null;
        this.periodDraftEnd = null;
        this.search(true);
    }

    formatPeriodRange(): string {
        const pad = (n: number) => ('0' + n).slice(-2);
        const s = this.nextVisitStartDate ? new Date(this.nextVisitStartDate) : null;
        const e = this.nextVisitEndDate ? new Date(this.nextVisitEndDate) : null;
        const dm = (d: Date) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
        const dmy = (d: Date) => `${dm(d)}/${d.getFullYear()}`;
        const dmy2 = (d: Date) => `${dm(d)}/${String(d.getFullYear()).slice(-2)}`;
        if (s && e) {
            return s.getFullYear() === e.getFullYear()
                ? `${dm(s)} – ${dmy(e)}`
                : `${dmy2(s)} – ${dmy2(e)}`;
        }
        if (s) {
            return `Desde ${dmy(s)}`;
        }
        if (e) {
            return `Hasta ${dmy(e)}`;
        }
        return 'Cualquier fecha';
    }

}
